import React, { useState, useEffect } from 'react';
import {
  X,
  ArrowRightLeft,
  RefreshCw,
  TrendingUp,
  Coins,
  Receipt,
} from 'lucide-react';
import { Language } from '../types/spot';

interface CurrencyConverterModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
}

const DEFAULT_EUR_TO_KRW_RATE = 1485;

export const CurrencyConverterModal: React.FC<CurrencyConverterModalProps> = ({
  isOpen,
  onClose,
  language,
}) => {
  const [exchangeRate, setExchangeRate] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('k_spotfinder_eur_krw_rate');
      if (saved) {
        const parsed = parseFloat(saved);
        if (!isNaN(parsed) && parsed > 500 && parsed < 3000) return parsed;
      }
    } catch {}
    return DEFAULT_EUR_TO_KRW_RATE;
  });

  const [isEditingRate, setIsEditingRate] = useState(false);
  const [tempRateInput, setTempRateInput] = useState(exchangeRate.toString());

  // Input states
  const [eurAmount, setEurAmount] = useState<string>('10');
  const [krwAmount, setKrwAmount] = useState<string>(
    Math.round(10 * exchangeRate).toLocaleString('de-DE')
  );
  const [activeSource, setActiveSource] = useState<'eur' | 'krw'>('eur');

  useEffect(() => {
    if (activeSource === 'eur') {
      const num = parseFloat(eurAmount.replace(',', '.')) || 0;
      const converted = Math.round(num * exchangeRate);
      setKrwAmount(converted > 0 ? converted.toLocaleString('de-DE') : '');
    } else {
      const cleanKrw = parseFloat(krwAmount.replace(/\./g, '').replace(/,/g, '')) || 0;
      const convertedEur = cleanKrw / exchangeRate;
      setEurAmount(convertedEur > 0 ? convertedEur.toFixed(2).replace('.', ',') : '');
    }
  }, [exchangeRate]);

  const handleEurChange = (val: string) => {
    setActiveSource('eur');
    setEurAmount(val);
    const num = parseFloat(val.replace(',', '.')) || 0;
    if (num <= 0) {
      setKrwAmount('');
      return;
    }
    const converted = Math.round(num * exchangeRate);
    setKrwAmount(converted.toLocaleString('de-DE'));
  };

  const handleKrwChange = (val: string) => {
    setActiveSource('krw');
    const rawVal = val.replace(/\D/g, '');
    if (!rawVal) {
      setKrwAmount('');
      setEurAmount('');
      return;
    }
    const num = parseInt(rawVal, 10);
    setKrwAmount(num.toLocaleString('de-DE'));
    const convertedEur = num / exchangeRate;
    setEurAmount(convertedEur.toFixed(2).replace('.', ','));
  };

  const handleAddEur = (add: number) => {
    setActiveSource('eur');
    const current = parseFloat(eurAmount.replace(',', '.')) || 0;
    const newTotal = current + add;
    setEurAmount(newTotal.toString().replace('.', ','));
    const converted = Math.round(newTotal * exchangeRate);
    setKrwAmount(converted.toLocaleString('de-DE'));
  };

  const handleAddKrw = (add: number) => {
    setActiveSource('krw');
    const current = parseFloat(krwAmount.replace(/\./g, '').replace(/,/g, '')) || 0;
    const newTotal = current + add;
    setKrwAmount(newTotal.toLocaleString('de-DE'));
    const convertedEur = newTotal / exchangeRate;
    setEurAmount(convertedEur.toFixed(2).replace('.', ','));
  };

  const handleClear = () => {
    setEurAmount('');
    setKrwAmount('');
  };

  const handleSaveCustomRate = () => {
    const parsed = parseFloat(tempRateInput.replace(',', '.'));
    if (!isNaN(parsed) && parsed > 500 && parsed < 3000) {
      setExchangeRate(parsed);
      localStorage.setItem('k_spotfinder_eur_krw_rate', parsed.toString());
      setIsEditingRate(false);
    }
  };

  const handleResetRate = () => {
    setExchangeRate(DEFAULT_EUR_TO_KRW_RATE);
    setTempRateInput(DEFAULT_EUR_TO_KRW_RATE.toString());
    localStorage.setItem('k_spotfinder_eur_krw_rate', DEFAULT_EUR_TO_KRW_RATE.toString());
    setIsEditingRate(false);
  };

  if (!isOpen) return null;

  const t = {
    de: {
      title: 'Währungsrechner',
      sub: 'EUR (€) ⇄ KRW (₩) Live-Umrechnung',
      rateLabel: 'Aktueller Richtkurs',
      editRate: 'Kurs anpassen',
      save: 'Speichern',
      reset: 'Standard',
      clear: 'Zurücksetzen',
      cheatTitle: 'Korea Preis-Orientierung (Spickzettel)',
      pricesYear: 'Preise 2025/2026',
      transferTooltip: 'Klicken zum Übertragen in Rechner',
      closeTooltip: 'Schließen',
      items: [
        { label: '🚇 T-Money U-Bahn Einzelfahrt', krw: 1500, note: 'Grundtarif U-Bahn & Bus' },
        { label: '☕ Iced Americano (Mega Coffee / Paik)', krw: 2000, note: 'Beliebte Coffee-Shops' },
        { label: '🥪 Isaac Toast / Egg Drop Toast', krw: 4500, note: 'Frühstück & Streetfood' },
        { label: '🍛 Uni-Mittagessen / Curry / Bibimbap', krw: 9000, note: 'Studenten-Essen Inha & Seoul' },
        { label: '🥩 K-BBQ Samgyeopsal Abendessen (p.P.)', krw: 18000, note: 'Koreanisches BBQ & Beilagen' },
        { label: '💄 Olive Young Shopping Einkauf', krw: 45000, note: 'K-Beauty & Skincare Essentials' },
        { label: '🚄 KTX Schnellzug Seoul ⇄ Busan', krw: 59800, note: 'Einfache Fahrt 300 km/h' },
      ],
    },
    en: {
      title: 'Currency Converter',
      sub: 'EUR (€) ⇄ KRW (₩) Live Calculator',
      rateLabel: 'Current Exchange Rate',
      editRate: 'Edit rate',
      save: 'Save',
      reset: 'Reset',
      clear: 'Clear',
      cheatTitle: 'Korea Spending Cheat Sheet',
      pricesYear: '2025/2026 Prices',
      transferTooltip: 'Click to transfer to calculator',
      closeTooltip: 'Close',
      items: [
        { label: '🚇 T-Money Subway Single Ride', krw: 1500, note: 'Base fare subway & bus' },
        { label: '☕ Iced Americano (Mega Coffee / Paik)', krw: 2000, note: 'Popular value coffee' },
        { label: '🥪 Isaac Toast / Egg Drop Toast', krw: 4500, note: 'Breakfast & street food' },
        { label: '🍛 Student Lunch / Curry / Bibimbap', krw: 9000, note: 'Inha & Seoul food hotspots' },
        { label: '🥩 K-BBQ Samgyeopsal Dinner (p.p.)', krw: 18000, note: 'Pork belly & side dishes' },
        { label: '💄 Olive Young Skincare Haul', krw: 45000, note: 'K-Beauty essentials' },
        { label: '🚄 KTX High-Speed Train Seoul ⇄ Busan', krw: 59800, note: 'One-way 300 km/h' },
      ],
    },
    kr: {
      title: '환율 계산기',
      sub: '유로 (€) ⇄ 원화 (₩) 실시간 변환',
      rateLabel: '현재 기준 환율',
      editRate: '환율 수정',
      save: '저장',
      reset: '초기화',
      clear: '지우기',
      cheatTitle: '한국 물가 가이드 (참고용)',
      pricesYear: '2025/2026 기준 물가',
      transferTooltip: '계산기에 금액 적용',
      closeTooltip: '닫기',
      items: [
        { label: '🚇 지하철/버스 기본 요금', krw: 1500, note: 'T-Money 1회권 기준' },
        { label: '☕ 메가커피/빽다방 아메리카노', krw: 2000, note: '가성비 커피' },
        { label: '🥪 이삭토스트 / 에그드랍', krw: 4500, note: '인기 토스트 샌드위치' },
        { label: '🍛 대학가 식사 / 커리 / 비빔밥', krw: 9000, note: '인하대 & 서울 대학가' },
        { label: '🥩 삼겹살 1인분 저녁 식사', krw: 18000, note: 'K-BBQ 및 밑반찬' },
        { label: '💄 올리브영 쇼핑', krw: 45000, note: 'K-뷰티 & 스킨케어' },
        { label: '🚄 KTX 서울 ⇄ 부산 편도', krw: 59800, note: '고속철도 편도 운임' },
      ],
    },
  }[language];

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <div
        className="currency-modal modal-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560, width: '100%' }}
      >
        {/* Header */}
        <div className="currency-modal__header">
          <div className="flex items-center gap-2">
            <div className="currency-modal__icon">
              <Coins style={{ width: 22, height: 22, color: 'var(--accent)' }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="currency-modal__title">{t.title}</h2>
                <span className="badge badge--neutral">EUR ⇄ KRW</span>
              </div>
              <p className="currency-modal__sub">{t.sub}</p>
            </div>
          </div>
          <button onClick={onClose} className="modal-close" title={t.closeTooltip}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Exchange Rate Bar */}
        <div className="currency-rate-bar">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-1.5 text-xs text-secondary">
              <TrendingUp style={{ width: 14, height: 14, color: 'var(--success)' }} />
              <span>{t.rateLabel}:</span>
              <strong className="text-primary font-mono" style={{ color: 'var(--accent)' }}>
                1 € = {exchangeRate.toLocaleString('de-DE')} ₩
              </strong>
            </div>

            <div className="flex items-center gap-2">
              {!isEditingRate ? (
                <button
                  onClick={() => {
                    setTempRateInput(exchangeRate.toString());
                    setIsEditingRate(true);
                  }}
                  className="currency-btn-tiny"
                >
                  {t.editRate}
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={tempRateInput}
                    onChange={(e) => setTempRateInput(e.target.value)}
                    className="currency-rate-input"
                  />
                  <button onClick={handleSaveCustomRate} className="currency-btn-tiny currency-btn-tiny--active">
                    {t.save}
                  </button>
                  <button onClick={handleResetRate} className="currency-btn-tiny" title="Standard">
                    <RefreshCw style={{ width: 11, height: 11 }} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Calculator Body */}
        <div className="currency-calc-body">
          {/* EUR Input Box */}
          <div className={`currency-input-card ${activeSource === 'eur' ? 'currency-input-card--active' : ''}`}>
            <div className="currency-input-card__top">
              <span className="currency-tag">🇪🇺 Euro (EUR)</span>
              <span className="currency-symbol">€</span>
            </div>
            <div className="currency-input-wrap">
              <input
                type="text"
                inputMode="decimal"
                value={eurAmount}
                onChange={(e) => handleEurChange(e.target.value)}
                placeholder="0"
                className="currency-input font-mono"
              />
              <span className="currency-suffix">EUR</span>
            </div>
            {/* Quick EUR Presets */}
            <div className="currency-presets">
              {[5, 10, 20, 50, 100].map((amount) => (
                <button
                  key={amount}
                  onClick={() => handleAddEur(amount)}
                  className="currency-preset-btn"
                >
                  +{amount}€
                </button>
              ))}
              <button onClick={handleClear} className="currency-preset-btn currency-preset-btn--clear">
                {t.clear}
              </button>
            </div>
          </div>

          {/* Swap Divider Indicator */}
          <div className="currency-swap-divider">
            <div className="currency-swap-badge">
              <ArrowRightLeft style={{ width: 14, height: 14, color: 'var(--accent)' }} />
            </div>
          </div>

          {/* KRW Input Box */}
          <div className={`currency-input-card ${activeSource === 'krw' ? 'currency-input-card--active' : ''}`}>
            <div className="currency-input-card__top">
              <span className="currency-tag">🇰🇷 Korean Won (KRW)</span>
              <span className="currency-symbol">₩</span>
            </div>
            <div className="currency-input-wrap">
              <input
                type="text"
                inputMode="numeric"
                value={krwAmount}
                onChange={(e) => handleKrwChange(e.target.value)}
                placeholder="0"
                className="currency-input font-mono"
              />
              <span className="currency-suffix">KRW</span>
            </div>
            {/* Quick KRW Presets */}
            <div className="currency-presets">
              {[5000, 10000, 30000, 50000, 100000].map((amount) => (
                <button
                  key={amount}
                  onClick={() => handleAddKrw(amount)}
                  className="currency-preset-btn"
                >
                  +{(amount / 1000).toFixed(0)}k₩
                </button>
              ))}
              <button onClick={handleClear} className="currency-preset-btn currency-preset-btn--clear">
                {t.clear}
              </button>
            </div>
          </div>
        </div>

        {/* Spending Cheat Sheet Section */}
        <div className="currency-cheatsheet">
          <div className="currency-cheatsheet__header">
            <div className="flex items-center gap-1.5">
              <Receipt style={{ width: 15, height: 15, color: 'var(--amber)' }} />
              <h3 className="currency-cheatsheet__title">{t.cheatTitle}</h3>
            </div>
            <span className="text-xs text-muted">{t.pricesYear}</span>
          </div>

          <div className="currency-cheatsheet__grid">
            {t.items.map((item, idx) => {
              const approxEur = (item.krw / exchangeRate).toFixed(2).replace('.', ',');
              return (
                <div
                  key={idx}
                  onClick={() => {
                    setActiveSource('krw');
                    setKrwAmount(item.krw.toLocaleString('de-DE'));
                    setEurAmount(approxEur);
                  }}
                  className="currency-cheat-item"
                  title={t.transferTooltip}
                >
                  <div className="currency-cheat-item__main">
                    <div className="currency-cheat-item__label">{item.label}</div>
                    <div className="currency-cheat-item__note">{item.note}</div>
                  </div>
                  <div className="currency-cheat-item__price">
                    <div className="currency-cheat-item__krw">{item.krw.toLocaleString('de-DE')} ₩</div>
                    <div className="currency-cheat-item__eur">≈ {approxEur} €</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
